"use client";

import { useState, useEffect, useRef } from "react";
import type { AnimationItem } from "lottie-web/build/player/lottie_svg";

/* Lottie loader — loop: false for hero, true for others.
 *
 * Player = lottie-web build SVG-only (`lottie_svg`, 236 KB vs 298 KB full —
 * renderer html/canvas dibuang, expressions & effects tetap) via dynamic
 * import → tetap keluar dari initial graph. Eks lottie-react (wrapper tipis
 * di atas lottie-web full): kontrol loadAnimation/play/pause/destroy
 * langsung, satu dependency kurang. `lottie_light` (164 KB) sengaja TIDAK
 * dipakai: 5/10 animasi memakai expressions (loopOut dll) yang tidak
 * didukung build light — risiko patah animasi (riset 2026-09-24, detail di
 * docs/superpowers/audits/2026-09-24-landing-performance-audit.md).
 *
 * Animasi JSON (160-860 KB/file) hanya di-fetch saat elemen mendekati
 * viewport (IntersectionObserver sticky) DAN ditunda ke idle
 * (requestIdleCallback) agar fetch+parse tidak membebani jalur kritis
 * (TBT/LCP). Observer tetap terpasang: pause() saat keluar viewport,
 * play() saat masuk — 9 animasi loop tidak berjalan bersamaan selamanya.
 * prefers-reduced-motion: fetch dilewati total. aria-hidden: dekoratif. */
export function LottieAnim({
  src,
  className,
  loop = true,
}: {
  src: string;
  className?: string;
  loop?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  /** sticky: pernah mendekati viewport → fetch JSON sekali. */
  const [seen, setSeen] = useState(false);
  /** kondisi viewport saat ini — autoplay awal saat player mount. */
  const visibleRef = useRef(false);
  const [data, setData] = useState<unknown>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const isIntersecting = !!entries[0]?.isIntersecting;
        visibleRef.current = isIntersecting;
        setSeen((prev) => prev || isIntersecting);
        if (isIntersecting) animRef.current?.play();
        else animRef.current?.pause();
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!seen) return;
    // Reduced motion → animasi dekoratif tidak dimuat sama sekali.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let cancelled = false;
    const run = () => {
      fetch(src)
        .then((r) => r.json())
        .then((d) => {
          if (!cancelled) setData(d);
        })
        .catch(() => {});
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number;
    let timeoutId: ReturnType<typeof setTimeout>;
    if (w.requestIdleCallback) idleId = w.requestIdleCallback(run, { timeout: 1500 });
    else timeoutId = setTimeout(run, 200);
    return () => {
      cancelled = true;
      if (w.cancelIdleCallback && idleId != null) w.cancelIdleCallback(idleId);
      else clearTimeout(timeoutId);
    };
  }, [seen, src]);

  // Player lifecycle: dibuat saat data tiba, di-destroy saat unmount/ganti
  // data. `visible` TIDAK masuk deps — play/pause dipegang IO via animRef;
  // autoplay dibaca dari visibleRef agar tidak me-recreate player.
  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    import("lottie-web/build/player/lottie_svg").then((mod) => {
      if (cancelled || !containerRef.current) return;
      animRef.current = mod.default.loadAnimation({
        container: containerRef.current,
        renderer: "svg",
        loop,
        autoplay: visibleRef.current,
        animationData: data,
      });
    });
    return () => {
      cancelled = true;
      animRef.current?.destroy();
      animRef.current = null;
    };
  }, [data, loop]);

  return <div ref={containerRef} className={className} aria-hidden="true" />;
}
